import React, { useEffect, useState } from 'react';
import { ModalBody, ModalFooter, Button, FormGroup, Label, Input, Row, Col } from 'reactstrap';
import Select from 'react-select';
import useProperList from '../../../hooks/useProperList';

interface Props {
  // Paso "Aprobar" o "Reagendar" del modal único de detalle — no es un modal
  // propio (evita modal-sobre-modal), sólo el body/footer de ese paso.
  onCancel: () => void;
  // `assignedProperId` acá es el id de `Proper` (lo que devuelve
  // GET /admin/propers) — user-service lo resuelve a profile.id antes de
  // guardar, ver AdminAdvisoryService.approve/reschedule.
  onConfirm: (assignedProperId: string, scheduledAt: string) => void;
  // Reusado también para "Reagendar" (mismo form: proper + fecha/hora) —
  // sólo cambian los textos y, al reagendar, el valor inicial de la fecha.
  confirmLabel?: string;
  confirmColor?: string;
  // Sólo prellena fecha/hora (reagendar) — el proper asignado no se puede
  // preseleccionar: `AdvisoryLead.assignedProper.id` es un id de `Profile`,
  // pero el selector de propers está keyed por `Proper.id` (ver nota en
  // AdminAdvisoryService.resolveProperProfileId) — no hay mapeo inverso
  // disponible client-side sin una llamada extra.
  initialScheduledAt?: string | null;
}

const ApproveAdvisoryLeadForm: React.FC<Props> = ({
  onCancel,
  onConfirm,
  confirmLabel = 'Aprobar y agendar',
  confirmColor = 'success',
  initialScheduledAt = null,
}) => {
  const { propersOptions: properOptions, isLoadingPropers } = useProperList();
  const [properId, setProperId] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');

  useEffect(() => {
    if (initialScheduledAt) {
      const initial = new Date(initialScheduledAt);
      setDate(initial.toISOString().slice(0, 10));
      setTime(initial.toTimeString().slice(0, 5));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfirm = () => {
    if (!properId || !date || !time) return;
    onConfirm(properId, new Date(`${date}T${time}`).toISOString());
  };

  return (
    <>
      <ModalBody>
        <FormGroup>
          <Label for="approve-advisory-proper">Proper asignado</Label>
          <Select
            inputId="approve-advisory-proper"
            options={properOptions}
            isLoading={isLoadingPropers}
            placeholder="Selecciona un proper"
            value={properOptions.find((o) => o.value === properId) ?? null}
            onChange={(option) => setProperId(option?.value ?? '')}
          />
        </FormGroup>
        <Row>
          <Col md={6}>
            <FormGroup>
              <Label for="approve-advisory-date">Fecha</Label>
              <Input
                type="date"
                id="approve-advisory-date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label for="approve-advisory-time">Hora</Label>
              <Input
                type="time"
                id="approve-advisory-time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
              />
            </FormGroup>
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={onCancel}>
          Volver
        </Button>
        <Button
          color={confirmColor}
          className="px-4"
          onClick={handleConfirm}
          disabled={!properId || !date || !time}
        >
          {confirmLabel}
        </Button>
      </ModalFooter>
    </>
  );
};

export default ApproveAdvisoryLeadForm;
